import {
    AvatarWithDisplayValueColumn,
    DataTable,
    usePagination,
} from 'src/components/DataTable';
import {
    EmployeeView,
    useActivateEmployeeMutation,
    useDeactivateEmployeeMutation,
    useGetEmployeesQuery,
} from 'src/api/endpoints/employees/api';
import { StudentLayout } from 'src/components/Layout/student/StudentLayout';
import { UserView } from 'src/api/endpoints/users/users.types.new';
import {
    _notify,
    NotificationTypes,
    notifyAboutException,
} from 'src/utils/notify';
import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import { LegacyModal } from 'src/components/Modal/LegacyModal';
import { Toolbar } from 'src/components/Toolbar';
import { Card } from 'src/components/Card/Card';
import { Helmet } from 'react-helmet-async';
import Loader from 'src/components/Loader';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ModalContent } from './Modal';
import classNames from 'classnames';
import {
    CreateEmployeePayload,
    UpdateEmployeePayload,
    useAddUserAccountMutation,
    useUpdateUserAccountMutation,
} from 'src/api/endpoints/users';
import { ResponseError } from 'src/api';
import { AuthorizedUserProps } from 'src/routes/components/PrivateRoute';

export interface UsersProps {
    directorCompany?: boolean;
}

export const Users: FC<UsersProps & AuthorizedUserProps> = (props) => {
    const {directorCompany = false} = props;

    const [modal, setModal] = useState(false);

    const [initialState, setInitialState] =
        useState<UpdateEmployeePayload | null>(null);

    const [selectedEmployee, selectEmployee] = useState<
        EmployeeView | null | undefined
    >(undefined);

    const toast: RefObject<Toast> = useRef<Toast>(null);

    const [add, { isLoading: isAddLoading }] = useAddUserAccountMutation();

    const [update, { isLoading: isUpdateLoading }] =
        useUpdateUserAccountMutation();

    const [deactivateEmployee, { isLoading: isEmployeeDeactivating }] =
        useDeactivateEmployeeMutation();
    const [activateEmployee, { isLoading: isEmployeeActivating }] =
        useActivateEmployeeMutation();

    const [config, setConfig] = usePagination();

    const { data, isLoading, isFetching, error } = useGetEmployeesQuery({
        params: config,
    });

    useEffect((): void => {
        if (!error) return;

        const responseError = error as ResponseError;

        notifyAboutException({
            toast,
            content: responseError.message,
        });
    }, [error]);

    function onCreate(form: CreateEmployeePayload) {
        return add(form)
            .unwrap()
            .then((): void => {
                setModal(false);
                setInitialState(null);
                selectEmployee(undefined);
                _notify(
                    toast,
                    {
                        type: NotificationTypes.Notification,
                        content: 'Сотрудник зарегистрирован в системе',
                    },
                    true
                );
            });
    }

    function onUpdate(form: UpdateEmployeePayload): Promise<void> {
        return update({
            ...form,
            password:
                form.password && form.password.trim().length > 0
                    ? form.password.trim()
                    : undefined,
        })
            .unwrap()
            .then((): void => {
                setModal(false);
                setInitialState(null);
                selectEmployee(undefined);

                _notify(
                    toast,
                    {
                        type: NotificationTypes.Notification,
                        content: 'Информация о сотруднике изменена',
                    },
                    true
                );
            });
    }

    const onEmployeeActivate = (employee: EmployeeView): void => {
        activateEmployee({ path: { employeeId: employee.id } })
            .unwrap()
            .then((): void => {
                setModal(false);
                setInitialState(null);
                selectEmployee(undefined);

                _notify(
                    toast,
                    {
                        type: NotificationTypes.Notification,
                        content: 'Сотрудник активирован',
                    },
                    true
                );
            });
    };

    const onEmployeeDeactivate = (employee: EmployeeView): void => {
        deactivateEmployee({ path: { employeeId: employee.id } })
            .unwrap()
            .then((): void => {
                setModal(false);
                setInitialState(null);
                selectEmployee(undefined);

                _notify(
                    toast,
                    {
                        type: NotificationTypes.Notification,
                        content: 'Сотрудник деактивирован',
                    },
                    true
                );
            });
    };

    const rowClassName = (employee: EmployeeView) =>
        classNames({
            'p-disabled': !(
                employee.canBeEdited ||
                employee.canBeDeactivated ||
                employee.canBeActivated
            ),
        });

    return (
        <StudentLayout auth={props}>
            <Helmet title={'Сотрудники'} />
            <Toast ref={toast} />
            <LegacyModal
                header={`${initialState ? 'Обновление' : 'Добавление'} пользователя`}
                visible={modal}
                onHide={(): void => {
                    setModal(false);

                    setInitialState(null);
                    selectEmployee(null);
                }}
            >
                {selectedEmployee !== undefined && (
                    <ModalContent
                        readOnly={
                            !(
                                selectedEmployee === null ||
                                (selectedEmployee &&
                                    selectedEmployee.canBeEdited)
                            )
                        }
                        onActivate={onEmployeeActivate}
                        onDeactivate={onEmployeeDeactivate}
                        employee={selectedEmployee}
                        initialState={initialState}
                        directorCompany={!!directorCompany}
                        onSubmit={initialState ? onUpdate : onCreate}
                        loading={
                            isUpdateLoading ||
                            isAddLoading ||
                            isEmployeeActivating ||
                            isEmployeeDeactivating
                        }
                    />
                )}
            </LegacyModal>
            <Card headerClassName={'mt-24'} header={'Сотрудники'}>
                {isLoading ? (
                    <Loader />
                ) : (
                    <DataTable
                        selectionMode="single"
                        loading={isFetching}
                        dataKey={'id'}
                        lazy={true}
                        value={data?.items}
                        onPagination={setConfig}
                        config={{
                            ...config,
                            totalRecords: data?.meta.total || 0,
                        }}
                        rowClassName={rowClassName}
                        isDataSelectable={event => {
                            const rowData =
                                event?.data as unknown as EmployeeView;

                            return (
                                rowData.canBeEdited ||
                                rowData.canBeDeactivated ||
                                rowData.canBeActivated
                            );
                        }}
                        onRowSelect={row => {
                            const rowData = row.data as EmployeeView;

                            selectEmployee(rowData);
                            setInitialState({
                                id: rowData.id,
                                role: rowData.role.id,
                                surname: rowData.surname,
                                filial: rowData.filial.id,
                                name: rowData.name,
                                password: '',
                            });

                            setModal(true);
                        }}
                        header={
                            <Toolbar
                                add={{
                                    loading: isAddLoading,
                                    onClick: () => {
                                        setModal(true);
                                        selectEmployee(null);
                                    },
                                }}
                            />
                        }
                    >
                        <Column
                            header="Сотрудник"
                            body={rowData => (
                                <AvatarWithDisplayValueColumn
                                    data={{
                                        userId: rowData.id,
                                        displayValue: `${rowData.surname} ${rowData.name}`,
                                    }}
                                />
                            )}
                        />
                        <Column header={'Логин'} field={'login'} />
                        <Column
                            header={'Статус'}
                            body={(rowData: EmployeeView): string =>
                                rowData.active ? 'Действующий' : 'Деактивирован'
                            }
                        />
                        <Column
                            header={'Роль'}
                            body={(rowData: UserView): string =>
                                rowData.role.name
                            }
                        />
                        {directorCompany ? (
                            <Column
                                header={'Филиал'}
                                body={(rowData: UserView): string =>
                                    rowData.filial.name
                                }
                            />
                        ) : null}
                    </DataTable>
                )}
            </Card>
        </StudentLayout>
    );
};
